/**
 * PORTED — RO-11-05 / #44590.
 * Origin: `excellent/app/_chrome/context-rail.tsx`.
 *
 * No next/* imports in the origin; depends on section-rail's useActiveHeading
 * and the local tree/marker nav ports. Copied into client/shell/chrome/ —
 * never import from app/ (PRD D6).
 */

import {
  useRailExtension,
  cn,
  type RailExtension,
  type RailExtensionTreeGroup,
} from "@excellent/ui";
import { RailMarkerNav, type RailMarkerNavItem } from "./rail-marker-nav";
import { RailTreeNav, type RailTreeNavGroup } from "./rail-tree-nav";
import { useActiveHeading } from "./section-rail";

/**
 * The right-hand contextual nav — the symmetric mirror of the primary ModeRail.
 *
 * ModeRail floats the modes (and their nested sub-views) in the reserved LEFT
 * margin well; ContextRail does the same on the RIGHT: a page pushes a
 * `side: "right"` rail extension (via `useProvideRailExtension`) — the docs
 * reader's doc list + on-this-page TOC, the analytics outline, or a NESTED TREE
 * (settings) — and it floats here in the reserved right gutter while the document
 * body stays centered like the rest of the app.
 *
 * It uses the SAME grammar as the left mode sub-nav: a guide hairline with a
 * single active marker that slides between rows (`RailMarkerNav`), and — for the
 * tree — each group's items revealed beneath it in the same accordion the left
 * ModeRail uses (`RailTreeNav`). Vertically centered (`justify-center`) like
 * ModeRail. Click-transparent except its links; desktop-only (< lg falls back to
 * the in-body disclosures the page renders).
 *
 * Placement: a `reserve` rail no longer pins to the far screen edge — it slides
 * IN to hug the centered content column's right edge (responsive, pure CSS — see
 * `reserveRightStyle`), so a page-contextual nav reads as belonging to the page
 * rather than floating off in the margin. On narrower viewports (gutter too
 * small to clear the content) it falls back to the edge, and below `lg` the
 * in-body nav takes over.
 */
export function ContextRail() {
  const extension = useRailExtension();
  const isRight = extension?.side === "right";

  // Hooks must run unconditionally. The scroll-spy watches whichever anchors are
  // live: a consolidated tree group's in-page sections, else the flat TOC's. (The
  // tree and the flat links/toc are mutually exclusive.)
  const tree = isRight ? extension!.tree : undefined;
  const toc = isRight ? extension!.toc : [];
  const activeHeading = useActiveHeading(
    tree ? treeAnchors(tree) : toc.flatMap((g) => [g.anchor, ...g.children.map((c) => c.anchor)]),
  );

  if (!extension || !isRight) return null;

  const reserve = extension.placement === "reserve";
  return (
    <nav
      aria-label={extension.groupLabel}
      // Marks a reserve rail floating in the right gutter so `.page-scroll-row`
      // (globals.css) widens `--content-inset` to keep the content ≥50px clear of
      // this wider (w-44) rail. Only reserve rails push into the gutter; a
      // `centered` rail floats past page-centre and needs no extra inset.
      data-context-rail={reserve ? "" : undefined}
      className="pointer-events-none absolute inset-y-0 right-0 z-30 hidden items-center justify-center lg:flex"
      // Offset: "centered" floats past page-centre (50% + the body's half-width);
      // "reserve" hugs the content column's right edge (see reserveRightStyle).
      style={
        reserve
          ? { right: reserveRightStyle(extension.reserveContentRem ?? 75) }
          : { left: `calc(50% + ${extension.tocOffsetRem ?? 22}rem)` }
      }
    >
      <div
        className={cn(
          "pointer-events-auto max-h-full space-y-7 overflow-y-auto py-6 no-scrollbar",
          reserve ? "w-44 px-2 pr-3" : "w-60 max-w-full px-2",
        )}
      >
        {/* Optional page-level control (e.g. a time-period <Select>), above the
            outline. The generic rail otherwise carries only links + toc. */}
        {extension.controls ? <div>{extension.controls}</div> : null}
        {tree ? (
          // The nested tree (settings): groups + the active group's items below it.
          <RailTreeNav
            groups={buildTreeGroups(tree, activeHeading, scrollToAnchor)}
            label={extension.groupLabel}
          />
        ) : (
          <>
            {/* The documents — a plain list with glyphs, NO track (they're docs,
                not in-page navigation points). The current doc reads bold. */}
            {extension.links.length > 0 ? (
              <RailMarkerNav items={buildDocItems(extension)} showLine={false} />
            ) : null}
            {/* On this page — the in-page nav track: guide line + sliding marker. */}
            {toc.length > 0 ? (
              <RailMarkerNav items={buildTocItems(toc, activeHeading, scrollToAnchor)} />
            ) : null}
          </>
        )}
      </div>
    </nav>
  );
}

/** Smooth-scroll a TOC/section anchor and record it in the URL hash. */
function scrollToAnchor(anchor: string) {
  const el = document.getElementById(anchor);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  history.replaceState(null, "", `#${anchor}`);
}

/** A consolidated active group's in-page anchors — what the scroll-spy watches. */
function treeAnchors(tree: RailExtensionTreeGroup[]): string[] {
  const active = tree.find((g) => g.active);
  if (!active || !active.consolidated) return [];
  return active.items.map((it) => it.anchor).filter((a): a is string => !!a);
}

/**
 * The tree (settings): groups with the active group's items nested beneath. A
 * consolidated group's items are in-page anchors (smooth-scroll + scroll-spy); a
 * multi-page group's items are leaf routes (active = the matched route).
 */
function buildTreeGroups(
  tree: RailExtensionTreeGroup[],
  activeHeading: string | null,
  onScroll: (anchor: string) => void,
): RailTreeNavGroup[] {
  return tree.map((g) => ({
    key: g.key,
    label: g.label,
    href: g.href,
    icon: g.icon,
    active: g.active,
    items: g.items.map((it): RailMarkerNavItem => {
      if (g.consolidated && it.anchor) {
        const anchor = it.anchor;
        return {
          key: anchor,
          label: it.label,
          href: `#${anchor}`,
          onClick: () => onScroll(anchor),
          active: g.active && anchor === activeHeading,
        };
      }
      return {
        key: it.href,
        label: it.label,
        href: it.href,
        active: g.active && it.href === g.activeItemHref,
      };
    }),
  }));
}

/**
 * The doc list — every doc, with its glyph, so the rail reads as "these are
 * docs". The active doc carries the marker; ungenerated docs read dim. The "new"
 * badge is dropped here (the dim style already signals it) so the narrow well
 * doesn't truncate distinct titles to the same stub; the full title is on hover.
 */
function buildDocItems(extension: RailExtension): RailMarkerNavItem[] {
  return extension.links.map((l) => ({
    key: `${l.href}|${l.label}`,
    label: l.label,
    icon: l.icon,
    href: l.href,
    active: l.href === extension.activeHref,
    dim: l.dim,
  }));
}

/**
 * The on-this-page outline — H2s, with the active section's H3s revealed beneath
 * it (collapsed otherwise, like the left sub-nav). The marker slides to whichever
 * heading is in view.
 */
function buildTocItems(
  toc: RailExtension["toc"],
  activeHeading: string | null,
  onScroll: (anchor: string) => void,
): RailMarkerNavItem[] {
  return toc.flatMap((g) => {
    const childActive = g.children.some((c) => c.anchor === activeHeading);
    const expanded = g.anchor === activeHeading || childActive;
    return [
      {
        key: g.anchor,
        label: g.text,
        href: `#${g.anchor}`,
        onClick: () => onScroll(g.anchor),
        active: g.anchor === activeHeading,
      },
      ...(expanded
        ? g.children.map((c) => ({
            key: c.anchor,
            label: c.text,
            href: `#${c.anchor}`,
            onClick: () => onScroll(c.anchor),
            active: c.anchor === activeHeading,
            indent: true,
          }))
        : []),
    ];
  });
}

/**
 * Hug the page's content column. The content (width `contentRem`, centered) has a
 * right margin of `(100% - content) / 2`; back off the rail's own width (w-44 =
 * 11rem = 176px) + a 50px gap so its left edge lands a constant 50px to the RIGHT
 * of the content edge on every viewport — a page-contextual nav attached to the
 * page, not floating at the screen edge. Floors to 0 (the screen edge) when the
 * gutter is too small to clear it — and there the content's `--content-inset`
 * (also `176px + 50px` when this rail is present) holds the same 50px, so the two
 * mechanisms agree and the rail never overlaps the content. The 50px matches the
 * `--content-inset` gap in globals.css — keep them in lockstep.
 */
function reserveRightStyle(contentRem: number): string {
  return `max(0px, calc((100% - ${contentRem}rem) / 2 - 176px - 50px))`;
}
